import React, { useState } from 'react';
import { reviewAccountRisk } from '../../../services/StrategiesService';
import { processError } from '../../../services/ServiceUtils';

const IMPACT_COLOR = { high: 'danger', medium: 'warning', low: 'secondary' };
// Direções permitidas pelo passe (viés a segurança): só ativar/apertar/observar — nunca afrouxar.
const DIRECTION = {
    enable: { label: 'Ativar', color: 'info' },
    tighten: { label: 'Apertar', color: 'primary' },
    observation: { label: 'Observação', color: 'secondary' }
};

function scoreColor(s) { return s >= 8 ? 'success' : s >= 5 ? 'warning' : 'danger'; }

/**
 * Revisão de POSTURA DE RISCO da conta por IA (read-only, human-in-the-loop). Diferente da
 * reavaliação por-estratégia: aqui a IA olha os controles de risco ACCOUNT-LEVEL (teto de
 * exposição, filtros BTC/pump-dump, proteção de grid, trava direcional...) + o estado do
 * portfólio e aponta gaps. Viés a SEGURANÇA: nunca sugere afrouxar — só ativar/apertar/observar.
 * As mudanças NÃO são aplicáveis daqui (o usuário ajusta nas Configurações). props: setNotification.
 */
function AccountRiskReview({ setNotification }) {
    const [loading, setLoading] = useState(false);
    const [review, setReview] = useState(null);
    const [snapshot, setSnapshot] = useState(null);
    const [expanded, setExpanded] = useState(false);

    function run() {
        setLoading(true);
        reviewAccountRisk()
            .then(data => {
                setReview({ summary: data.summary, riskScore: data.riskScore, findings: data.findings || [] });
                setSnapshot(data.snapshot || null);
                setExpanded(true);
            })
            .catch(err => setNotification && setNotification({ type: 'danger', text: processError(err) }))
            .finally(() => setLoading(false));
    }

    const exp = snapshot && snapshot.portfolio && snapshot.portfolio.exposure;
    const grids = snapshot && snapshot.gridProtection;

    return (
        <div className='card card-body border-0 shadow mb-3'>
            <div className='d-flex align-items-center flex-wrap gap-2'>
                <span style={{ fontSize: '1.1em' }}>🛡️</span>
                <strong>Revisão de gestão de risco</strong>
                <span className='text-muted small'>todos os mercados</span>
                {review && (
                    <span className={`badge bg-${scoreColor(review.riskScore)}`} title='Pontuação de proteção (10 = bem protegido)'>
                        Proteção {review.riskScore}/10
                    </span>
                )}
                <div className='ms-auto d-flex gap-2'>
                    {review && (
                        <button className='btn btn-sm btn-outline-secondary' onClick={() => setExpanded(e => !e)}>
                            {expanded ? 'Ocultar' : 'Ver detalhes'}
                        </button>
                    )}
                    <button className='btn btn-sm btn-primary' onClick={run} disabled={loading}>
                        {loading ? 'Analisando…' : review ? 'Revisar de novo' : 'Revisar com IA'}
                    </button>
                </div>
            </div>

            {!review && !loading && (
                <small className='text-muted d-block mt-2'>
                    A IA analisa seus controles de risco globais (teto de exposição, filtros, proteção de grid,
                    trava direcional) e o estado do portfólio, e aponta proteções desligadas ou frouxas.
                    É só sugestão — você ajusta nas Configurações. Nunca propõe afrouxar um controle.
                </small>
            )}

            {review && expanded && (
                <div className='mt-3'>
                    <p className='mb-2'>{review.summary}</p>

                    {snapshot && (
                        <div className='d-flex flex-wrap gap-3 small text-muted mb-3'>
                            {exp
                                ? <span>Exposição: <strong className={exp.exposurePct > (snapshot.riskKnobs.maxExposurePercent || 100) ? 'text-danger' : ''}>{exp.exposurePct}%</strong>{snapshot.riskKnobs.maxExposurePercent ? ` (teto ${snapshot.riskKnobs.maxExposurePercent}%)` : ' (sem teto)'}</span>
                                : <span>Exposição: <strong>—</strong> (estratégia parada)</span>}
                            {grids && <span>Grids: <strong>{grids.activeGrids}</strong> ({grids.doomStopDisabled === 0 ? 'todos protegidos' : `${grids.doomStopDisabled} sem doom-stop`})</span>}
                            {snapshot.portfolio && <span>Estratégias: <strong>{snapshot.portfolio.activeStrategies.spot}</strong> spot · <strong>{snapshot.portfolio.activeStrategies.futures}</strong> futuros</span>}
                            {snapshot.portfolio && snapshot.portfolio.marketOverlapSymbols && snapshot.portfolio.marketOverlapSymbols.length > 0 &&
                                <span className='text-warning'>Overlap spot×futuros: {snapshot.portfolio.marketOverlapSymbols.join(', ')}</span>}
                        </div>
                    )}

                    {review.findings.length === 0 ? (
                        <div className='alert alert-success py-2 small mb-0'>✓ Nenhum gap relevante — a postura de risco está sólida.</div>
                    ) : (
                        <ul className='list-group'>
                            {review.findings.map(f => {
                                const dir = DIRECTION[f.direction] || DIRECTION.observation;
                                return (
                                    <li key={f.id} className='list-group-item'>
                                        <div className='d-flex align-items-center flex-wrap gap-2 mb-1'>
                                            <span className={`badge bg-${IMPACT_COLOR[f.impact] || 'secondary'}`}>{f.impact}</span>
                                            <span className={`badge bg-${dir.color}`}>{dir.label}</span>
                                            <strong>{f.label || f.knob}</strong>
                                            {f.direction !== 'observation' && f.suggestedValue != null && (
                                                <span className='small text-muted'>
                                                    <code>{f.currentValue}</code> → <code className='text-success'>{f.suggestedValue}</code>
                                                </span>
                                            )}
                                        </div>
                                        <div className='small text-muted'>{f.rationale}</div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}

export default AccountRiskReview;
